/** Display helpers shared across the app. */

export const fmtDateTime = (value: string | null | undefined): string =>
  value
    ? new Date(value).toLocaleString([], {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

export const fmtDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleDateString() : "";

/** A `date` column is a plain day — parse it locally, never as UTC midnight. */
/**
 * A date, from either a `date` column or a full timestamp.
 *
 * The `T00:00:00` is what makes a bare `2026-08-27` read as local midnight
 * rather than UTC midnight, which on any machine west of Greenwich would show
 * the previous day. But it was being appended to whole timestamps too —
 * `2026-08-27 14:30:32.711513+00T00:00:00` is not a date in any calendar, and
 * the Integrations screen showed "Invalid Date" against every invoice and
 * against the connection itself.
 *
 * So the date part is taken first, however much else came with it: everything
 * before the space or the T that separates a Postgres timestamp from its time.
 */
export const fmtDateOnly = (value: string | null | undefined): string => {
  const day = (value ?? "").trim().split(/[ T]/)[0] ?? "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return "";
  const at = new Date(`${day}T00:00:00`);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleDateString();
};

/** For an <input type="datetime-local"> value. */
export function toLocalDateTimeInput(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
}

/** For an <input type="date"> bound to a timestamp column. */
export function toLocalDateInput(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The day a person picked, as the instant it begins where they are.
 *
 * Local midnight, not UTC: a follow-up set for Friday should stop covering the
 * job when Friday starts in Alabama, not five hours earlier.
 */
export function fromLocalDateInput(value: string): string | null {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toISOString();
}

export function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

export const streetViewUrl = (record: {
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string =>
  record.lat && record.lng
    ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${record.lat},${record.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [record.address, record.city, record.state, record.zip]
          .filter(Boolean)
          .join(", "),
      )}`;

export const zillowUrl = (address: string): string =>
  `https://www.zillow.com/homes/${encodeURIComponent(
    address.replace(/,/g, " ").replace(/\s+/g, "-"),
  )}_rb/`;
